import { useEffect, useRef, useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent, Button, Alert, Input, DocumentScanner } from '../components'
import {
  loadPortal,
  uploadPortalDocument,
  startPortalIdentityCheck,
  submitPortalManualIdentityCheck,
  Portal,
  PortalCredentials,
} from '../services/portalLinkService'
import { IDENTITY_DOC_LABELS as DOC_TYPE_LABELS, IDENTITY_DOC_TIERS, IDENTITY_DOC_SHAPE, IdentityDocType } from '../identityCheckPoints'

interface PortalPageProps {
  token: string
}

type Stage = 'gate' | 'ready'

const CREDS_KEY_PREFIX = 'mc_portal_creds_'

// Doc types offered for the manual "add a supporting document" flow below —
// excludes 'passport'/'drivers-licence'/'other', which are covered by the
// Didit hosted session (or aren't a real supporting-document type).
const MANUAL_DOC_TYPE_VALUES: IdentityDocType[] = [
  'birth-certificate',
  'citizenship-certificate',
  'proof-of-age-card',
  'tertiary-student-id',
  'government-employee-id',
  'medicare-card',
  'bank-statement',
  'utility-bill',
  'council-rates-notice',
  'property-lease-agreement',
]
const MANUAL_DOC_TYPES: { value: IdentityDocType; label: string }[] = MANUAL_DOC_TYPE_VALUES.map((value) => ({
  value,
  label: DOC_TYPE_LABELS[value],
}))

const formatDate = (value?: string | null) => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export const PortalPage: React.FC<PortalPageProps> = ({ token }) => {
  const [stage, setStage] = useState<Stage>('gate')
  const [email, setEmail] = useState('')
  const [settlementDate, setSettlementDate] = useState('')
  const [gateError, setGateError] = useState<string | null>(null)
  const [gateSubmitting, setGateSubmitting] = useState(false)
  const [portal, setPortal] = useState<Portal | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [startingVerification, setStartingVerification] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(null)
  const [manualDocType, setManualDocType] = useState(MANUAL_DOC_TYPES[0].value)
  const [submittingManual, setSubmittingManual] = useState(false)
  const [manualError, setManualError] = useState<string | null>(null)
  // Only used when manualDocType === 'medicare-card' — sent alongside the
  // photo so the backend can run Didit's Australia Medicare database
  // validation (name + DOB + card number match) instead of just queuing the
  // photo for staff review. See buildMedicareCheckFields in backend/src/index.ts.
  const [medicareCardNumber, setMedicareCardNumber] = useState('')
  const [medicareDob, setMedicareDob] = useState('')
  const [scannerOpen, setScannerOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const manualFileInputRef = useRef<HTMLInputElement>(null)
  const credsRef = useRef<PortalCredentials | null>(null)

  // Didit's hosted-verification redirect (see backend/src/index.ts) lands
  // here with these appended — it's an unsigned browser redirect, not the
  // authoritative result (that's the separate signed webhook), so it's shown
  // as a heads-up only; the identityChecks list below reflects the real,
  // confirmed status once it's loaded.
  const redirectStatus = new URLSearchParams(window.location.search).get('status')

  const attemptLoad = async (credentials: PortalCredentials) => {
    setGateError(null)
    setGateSubmitting(true)
    try {
      const result = await loadPortal(token, credentials)
      credsRef.current = credentials
      try {
        sessionStorage.setItem(`${CREDS_KEY_PREFIX}${token}`, JSON.stringify(credentials))
      } catch {
        // non-fatal — just means they'll need to re-enter details next visit
      }
      setPortal(result)
      setStage('ready')
    } catch (err) {
      setGateError(err instanceof Error ? err.message : 'We could not verify your details')
      setStage('gate')
    } finally {
      setGateSubmitting(false)
    }
  }

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(`${CREDS_KEY_PREFIX}${token}`)
      if (stored) {
        const credentials = JSON.parse(stored) as PortalCredentials
        setEmail(credentials.email)
        setSettlementDate(credentials.settlementDate)
        attemptLoad(credentials)
        return
      }
    } catch {
      // fall through to the gate form
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const handleGateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    attemptLoad({ email: email.trim(), settlementDate: settlementDate.trim() })
  }

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !credsRef.current) return

    setUploadError(null)
    setUploading(true)
    try {
      const document = await uploadPortalDocument(token, credsRef.current, file)
      setPortal((prev) => (prev ? { ...prev, documents: [document, ...prev.documents] } : prev))
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  const handleStartVerification = async () => {
    if (!credsRef.current) return

    setVerificationError(null)
    setStartingVerification(true)
    try {
      const { identityCheck, verificationUrl } = await startPortalIdentityCheck(token, credsRef.current)
      setPortal((prev) => (prev ? { ...prev, identityChecks: [identityCheck, ...prev.identityChecks] } : prev))
      if (verificationUrl) window.open(verificationUrl, '_blank', 'noopener')
    } catch (err) {
      setVerificationError(err instanceof Error ? err.message : 'Failed to start verification')
    } finally {
      setStartingVerification(false)
    }
  }

  const isMedicareDocType = manualDocType === 'medicare-card'
  // Drives which guide box DocumentScanner shows — a card-sized box for ID
  // cards, an A4-shaped box for full-page documents. See identityCheckPoints.ts.
  const manualDocShape = IDENTITY_DOC_SHAPE[manualDocType]
  // Required before we'll even open the scanner/file picker for a Medicare
  // card, so the automatic database check (see submitPortalManualIdentityCheck)
  // always has what it needs rather than silently falling back to pending.
  const medicareFieldsReady = !isMedicareDocType || (medicareCardNumber.trim() !== '' && medicareDob.trim() !== '')

  // Uploads a file, then tags it as a supporting ID document — together
  // these two calls are the manual half of the 100-point identity check
  // (the Didit session above covers the primary photo-ID half). See
  // backend/src/identity-check-points.ts for how the points are scored.
  // Shared by both capture paths below: the native file input's onChange
  // and DocumentScanner's onCapture.
  const submitManualDocument = async (file: File) => {
    if (!credsRef.current) return

    setManualError(null)
    setSubmittingManual(true)
    try {
      const document = await uploadPortalDocument(token, credsRef.current, file)
      const identityCheck = await submitPortalManualIdentityCheck(token, credsRef.current, {
        docType: manualDocType,
        documentId: document.id,
        ...(isMedicareDocType ? { medicareCardNumber: medicareCardNumber.trim(), dateOfBirth: medicareDob.trim() } : {}),
      })
      setPortal((prev) => (prev ? { ...prev, identityChecks: [identityCheck, ...prev.identityChecks] } : prev))
      setMedicareCardNumber('')
      setMedicareDob('')
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Failed to submit document')
    } finally {
      setSubmittingManual(false)
    }
  }

  const handleManualFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    await submitManualDocument(file)
  }

  const handleScannerCapture = async (file: File) => {
    setScannerOpen(false)
    await submitManualDocument(file)
  }

  const completedCount = portal?.phases.filter((p) => p.status === 'completed').length ?? 0
  const totalCount = portal?.phases.length ?? 0

  // Step 1 ("verify your photo ID") state, derived from the Didit checks
  // only — the manual/supporting-document checks below are step 2.
  const diditChecks = portal?.identityChecks.filter((c) => c.method === 'didit') ?? []
  const step1Verified = diditChecks.some((c) => c.status === 'verified')
  const step1Pending = !step1Verified && diditChecks.some((c) => c.status === 'pending')
  const step1Rejected = !step1Verified && !step1Pending && diditChecks.length > 0
  const step1NotStarted = diditChecks.length === 0
  const step1VerificationUrl = diditChecks.find((c) => c.status === 'pending')?.verificationUrl ?? null
  const step1Points = diditChecks.find((c) => c.status === 'verified')?.points ?? null

  // Step 2 ("add a supporting document") is shown once step 1 is verified
  // and the same 100-point summary the tenant panel uses (pointsSummary,
  // from backend/src/identity-check-points.ts) says more is still needed —
  // so both sides of the portal agree on what "done" means.
  const manualChecks = portal?.identityChecks.filter((c) => c.method !== 'didit') ?? []
  const showStep2 = step1Verified && !portal?.pointsSummary.met
  const manualPending = manualChecks.some((c) => c.status === 'pending')

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            {portal ? `Welcome, ${portal.clientName}` : 'Your Matter Portal'}
          </h1>
          {portal && (
            <p className="text-sm text-gray-600 mt-1">
              {portal.matterType} &middot; {portal.propertyAddress}
            </p>
          )}
        </div>

        {stage === 'gate' && (
          <Card>
            <CardHeader>
              <CardTitle>Verify It's You</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Enter your email address and this matter's settlement date to continue.
              </p>
              {gateError && <Alert type="error" className="mb-4">{gateError}</Alert>}
              <form onSubmit={handleGateSubmit} className="space-y-3">
                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Input
                  label="Settlement Date"
                  type="date"
                  value={settlementDate}
                  onChange={(e) => setSettlementDate(e.target.value)}
                  required
                />
                <Button type="submit" className="w-full" disabled={gateSubmitting}>
                  {gateSubmitting ? 'Checking...' : 'Continue'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {stage === 'ready' && portal && (
          <>
            {redirectStatus && (
              <Alert type="info">
                Your verification was submitted (reported as "{redirectStatus}"). It can take a few
                minutes to confirm — the status below will update once it does.
              </Alert>
            )}

            <Card>
              <CardHeader>
                <CardTitle>
                  Steps {totalCount > 0 && <span className="text-gray-400 font-normal">({completedCount}/{totalCount})</span>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {portal.phases.length === 0 ? (
                  <p className="text-sm text-gray-500">No steps yet</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {portal.phases.map((phase) => (
                      <li key={phase.id} className="py-2.5 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span
                            className={`shrink-0 h-5 w-5 rounded-full flex items-center justify-center text-xs ${
                              phase.status === 'completed'
                                ? 'bg-emerald-100 text-emerald-700'
                                : phase.status === 'current'
                                  ? 'bg-primary/10 text-primary'
                                  : 'bg-gray-100 text-gray-400'
                            }`}
                          >
                            {phase.status === 'completed' ? '✓' : phase.status === 'current' ? '●' : ''}
                          </span>
                          <span
                            className={`text-sm truncate ${
                              phase.status === 'completed'
                                ? 'text-gray-500 line-through'
                                : phase.status === 'current'
                                  ? 'text-gray-900 font-medium'
                                  : 'text-gray-400'
                            }`}
                          >
                            {phase.label}
                          </span>
                        </div>
                        {phase.dueDate && phase.status !== 'completed' && (
                          <span className="text-xs text-gray-400 shrink-0">Due {formatDate(phase.dueDate)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>ID Verification</CardTitle>
              </CardHeader>
              <CardContent>
                {verificationError && <Alert type="error" className="mb-3">{verificationError}</Alert>}
                {manualError && <Alert type="error" className="mb-3">{manualError}</Alert>}

                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm font-medium text-gray-900">
                    {portal.pointsSummary.totalPoints} / {portal.pointsSummary.required} points
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${portal.pointsSummary.met ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                    {portal.pointsSummary.met ? 'Complete' : 'In progress'}
                  </span>
                </div>

                <details className="mb-4 border border-gray-100 rounded-lg">
                  <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-gray-700">
                    How points are calculated
                  </summary>
                  <div className="px-3 pb-3 space-y-2">
                    {IDENTITY_DOC_TIERS.map((tier) => (
                      <div key={tier.title}>
                        <p className="text-xs font-medium text-gray-800">
                          {tier.title} — {tier.points} pts each <span className="font-normal text-gray-400">({tier.note})</span>
                        </p>
                        <p className="text-xs text-gray-500">{tier.types.map((t) => DOC_TYPE_LABELS[t]).join(', ')}</p>
                      </div>
                    ))}
                  </div>
                </details>

                <ul className="space-y-4">
                  <li className="flex items-start gap-2.5">
                    <span
                      className={`shrink-0 h-5 w-5 mt-0.5 rounded-full flex items-center justify-center text-xs ${
                        step1Verified ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {step1Verified ? '✓' : ''}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium ${step1Verified ? 'text-gray-500' : 'text-gray-900'}`}>
                        Verify your photo ID
                        {step1Verified && <span className="text-xs font-normal text-gray-500"> · {step1Points} pts</span>}
                      </p>
                      {step1NotStarted && (
                        <div className="mt-1.5">
                          <Button type="button" size="sm" onClick={handleStartVerification} disabled={startingVerification}>
                            {startingVerification ? 'Starting...' : 'Start Verification'}
                          </Button>
                        </div>
                      )}
                      {step1Pending && (
                        <div className="mt-0.5">
                          <p className="text-xs text-gray-500">This can take a few minutes to confirm.</p>
                          {step1VerificationUrl && (
                            <a
                              href={step1VerificationUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline mt-0.5 inline-block"
                            >
                              Continue verification →
                            </a>
                          )}
                        </div>
                      )}
                      {step1Rejected && (
                        <div className="mt-1.5">
                          <p className="text-xs text-gray-500 mb-1.5">We couldn't verify your ID — please try again.</p>
                          <Button type="button" size="sm" variant="secondary" onClick={handleStartVerification} disabled={startingVerification}>
                            {startingVerification ? 'Starting...' : 'Try Again'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </li>

                  {showStep2 && (
                    <li className="flex items-start gap-2.5">
                      <span className="shrink-0 h-5 w-5 mt-0.5 rounded-full flex items-center justify-center text-xs bg-gray-100 text-gray-400" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {portal.pointsSummary.hasPrimary ? 'Add a supporting document' : 'Add another ID document'}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 mb-1.5">
                          {manualPending
                            ? "We're reviewing your document. You can add another below if you have one."
                            : portal.pointsSummary.hasPrimary
                              ? 'One more document is needed to complete your verification, e.g. a Medicare card, bank statement, or utility bill.'
                              : "A driver's licence alone isn't enough — please also add a birth certificate or citizenship certificate, plus one more supporting document if needed."}
                        </p>
                        {manualChecks.length > 0 && (
                          <ul className="mb-2 space-y-1">
                            {manualChecks.map((c) => (
                              <li key={c.id} className="text-xs text-gray-600 flex items-center gap-1.5">
                                <span>{DOC_TYPE_LABELS[c.docType as IdentityDocType] ?? c.docType}</span>
                                <span className="text-gray-400">
                                  {c.status === 'verified' && `· verified · ${c.points} pts`}
                                  {c.status === 'pending' && '· reviewing'}
                                  {c.status === 'rejected' && '· not accepted'}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <select
                            value={manualDocType}
                            onChange={(e) => setManualDocType(e.target.value as IdentityDocType)}
                            className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
                            disabled={submittingManual}
                          >
                            {MANUAL_DOC_TYPES.map((t) => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>

                        {isMedicareDocType && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2 max-w-md">
                            <Input
                              label="Medicare card number"
                              value={medicareCardNumber}
                              onChange={(e) => setMedicareCardNumber(e.target.value)}
                              disabled={submittingManual}
                              placeholder="e.g. 2950 XXXXX 1"
                            />
                            <Input
                              label="Date of birth"
                              type="date"
                              value={medicareDob}
                              onChange={(e) => setMedicareDob(e.target.value)}
                              disabled={submittingManual}
                            />
                          </div>
                        )}
                        {isMedicareDocType && (
                          <p className="text-xs text-gray-500 mb-2">
                            We check these against the Medicare database automatically — take a photo of the front
                            of your card below to finish.
                          </p>
                        )}

                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            ref={manualFileInputRef}
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            onChange={handleManualFileSelected}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => setScannerOpen(true)}
                            disabled={submittingManual || !medicareFieldsReady}
                          >
                            {submittingManual ? 'Submitting...' : `Scan ${manualDocShape === 'card' ? 'Card' : 'Document'}`}
                          </Button>
                          {medicareFieldsReady && (
                            <button
                              type="button"
                              className="text-xs text-primary hover:underline"
                              onClick={() => manualFileInputRef.current?.click()}
                              disabled={submittingManual}
                            >
                              or upload a file instead
                            </button>
                          )}
                          {isMedicareDocType && !medicareFieldsReady && (
                            <span className="text-xs text-gray-400">Enter your card number and date of birth first</span>
                          )}
                        </div>
                      </div>
                    </li>
                  )}
                </ul>

                {portal.pointsSummary.met && (
                  <p className="text-sm text-emerald-700 font-medium mt-4">✓ Your identity has been verified.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Your Documents</CardTitle>
              </CardHeader>
              <CardContent>
                {uploadError && <Alert type="error" className="mb-3">{uploadError}</Alert>}
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelected} />
                <Button type="button" size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Upload a File'}
                </Button>
                <div className="pt-4 mt-4 border-t border-gray-100">
                  {portal.documents.length === 0 ? (
                    <p className="text-sm text-gray-500">No files uploaded yet</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {portal.documents.map((doc) => (
                        <li key={doc.id} className="py-2.5">
                          <p className="text-sm font-medium text-gray-900">{doc.fileName}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {doc.uploadedBy === 'client' ? 'Uploaded by you' : 'Uploaded by your conveyancer'} · {formatDate(doc.createdAt)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <DocumentScanner
        open={scannerOpen}
        title={`Scan your ${DOC_TYPE_LABELS[manualDocType]}`}
        shape={manualDocShape}
        onCapture={handleScannerCapture}
        onCancel={() => setScannerOpen(false)}
      />
    </div>
  )
}
